import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { SupportTicketApiModel } from 'src/app/models/api/support/supportTicketApiModel';
import { SupportTicketSeverity } from 'src/app/models/api/support/supportTicketSeverity.enum';
import { MaxNumberOfSupportFiles, MaxSupportFileSizeInMB } from 'src/app/models/constants/support.constants';
import { ISelectOption } from 'src/app/models/selectOptions';
import { SupportTicketSeveritySelectOptions } from 'src/app/models/selectOptions/supportTicketSeveritySelectOptions.model';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { SupportHttpService } from 'src/app/services/httpServices/supportHttpService/support-http.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';

@Component({
  selector: 'app-support',
  templateUrl: './support.component.html',
  styleUrls: ['./support.component.scss'],
})
export class SupportComponent {
  public submitAttempt = false;
  public severityOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public severityValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public waitingForResponse = false;
  public selectedFiles?: FileList = undefined;
  public fileUploaderValueDescription = '';
  public fileUploaderValueTooltip = '';
  public showSuccessMessage = false;
  public supportTicketSeverity = SupportTicketSeverity;

  public readonly TITLE_MAXIMUM_LENGTH: number = 500;
  public readonly DESC_MAXIMUM_LENGTH: number = 1500;
  public readonly PHONE_INPUT_PLACEHOLDER: string = '+XXXXXXXXXX';
  public readonly ALLOWED_SUPPORT_FILE_TYPES = ['png', 'jpeg', 'jpg', 'txt', 'json'];

  public fg: FormGroup = new FormGroup({
    title: new FormControl('', [Validators.required, Validators.maxLength(this.TITLE_MAXIMUM_LENGTH)]),
    description: new FormControl('', [Validators.required, Validators.maxLength(this.DESC_MAXIMUM_LENGTH)]),
    severity: new FormControl('', Validators.required),
  });

  constructor(
    private readonly _supportHttpService: SupportHttpService,
    private readonly _notificationService: NotificationsService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _supportTicketSeveritySelectOptions: SupportTicketSeveritySelectOptions
  ) {
    this.severityOptions$.next(this._supportTicketSeveritySelectOptions.generate());
  }

  public severityOnSelection(option?: ISelectOption) {
    this._updateSelectControl('severity', option);
    if (option) {
      this.severityValid$.next(this.fg.controls['severity'].valid);
    }
  }

  public create() {
    this.severityValid$.next(this.fg.controls['severity'].valid);

    if (this.fg.invalid) {
      this.submitAttempt = true;
      return;
    }

    this.submitAttempt = false;
    this.waitingForResponse = true;

    const formData = new FormData();

    const newTicket = {
      title: this.fg.controls['title'].value,
      description: this.fg.controls['description'].value,
      severity: this.fg.controls['severity'].value,
    } as SupportTicketApiModel;

    if (this.fg.controls['phone_number'] != null) {
      newTicket.phone_number = this.fg.controls['phone_number'].value;
    }

    if (this.selectedFiles && this.selectedFiles.length > 0) {
      for (let i = 0; i < this.selectedFiles.length; i++) formData.append('files', this.selectedFiles[i]);

      this._supportHttpService.uploadFiles(formData).subscribe({
        next: (token) => {
          newTicket.uploads_token = token;
          this._createTicket(newTicket);
        },
        error: () => {
          this.waitingForResponse = false;
          this._notificationService.error(`Failed to upload files for support ticket`);
        },
      });
    } else {
      this._createTicket(newTicket);
    }
  }

  public onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList = element.files;
    if (fileList == null) {
      return;
    }

    if (fileList.length > MaxNumberOfSupportFiles) {
      this._notificationService.error(`Cannot upload more than ${MaxNumberOfSupportFiles} files.`);
      this._resetFileOption(event);
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const currentFile: File = fileList[i];

      const splittedFileName: string[] = currentFile.name.split('.');
      const file_suffix = splittedFileName[splittedFileName.length - 1];
      if (!this.ALLOWED_SUPPORT_FILE_TYPES.includes(file_suffix)) {
        this._notificationService.error(`The allowed file types are ${this.ALLOWED_SUPPORT_FILE_TYPES.join(', ')}`);
        this._resetFileOption(event);
        return;
      }

      if (this._convertSizeFromBytesToMB(currentFile.size) > MaxSupportFileSizeInMB) {
        this._notificationService.error(
          `File '${currentFile.name}' is too big. Single file size shouldn't be more than ${MaxSupportFileSizeInMB}MB`
        );
        this._resetFileOption(event);
        return;
      }
    }

    this.selectedFiles = fileList;
    this._setFileSelectionText(this.selectedFiles);
  }

  public openFileSelectionDialog(): void {
    const fileUploaderElement = document.getElementById('#SupportHiddenFileUploader') as HTMLInputElement;
    if (fileUploaderElement) {
      fileUploaderElement.click();
    } else {
      console.error('File uploader component is missing');
    }
  }

  public toggleCriticalSeverityApproval() {
    this.fg.controls['criticalSeverityApproved'].setValue(!this.fg.controls['criticalSeverityApproved'].value);
  }

  private _createTicket(ticket: SupportTicketApiModel): void {
    this._supportHttpService.createSupportTicket(ticket).subscribe({
      next: () => {
        this.waitingForResponse = false;
        this.showSuccessMessage = true;
      },
      error: (httpError: HttpErrorResponse) => {
        this.waitingForResponse = false;
        if (httpError.status === 500) {
          this._notificationService.error(
            'We were unable to create a support ticket at this time. Please reach out to us at : support@zenity.io or try again at a later time'
          );
        } else {
          this._httpErrorHandlerService.handle(httpError);
        }
      },
    });
  }

  private _resetFileOption(event: Event) {
    const element = event.currentTarget as HTMLInputElement;
    element.files = null;
    this.selectedFiles = undefined;
    this._setFileSelectionText(undefined);
  }

  private _updateSelectControl(controlName: string, option?: ISelectOption): void {
    if (option) {
      this.fg.controls[controlName].setValue(option.id);
    } else {
      this.fg.controls[controlName].setValue(undefined);
    }

    if (option?.id === SupportTicketSeverity.urgent) {
      this.fg.addControl('criticalSeverityApproved', new FormControl(false, Validators.requiredTrue));
      this.fg.addControl(
        'phone_number',
        new FormControl('', [Validators.required, Validators.pattern(new RegExp(/^\+[1-9]\d{3,14}$/))])
      );
    } else {
      this.fg.removeControl('criticalSeverityApproved');
      this.fg.removeControl('phone_number');
    }
  }

  private _convertSizeFromBytesToMB(sizeInBytes: number): number {
    return sizeInBytes / 1024 / 1024;
  }

  private _setFileSelectionText(selectedFiles?: FileList): void {
    if (!selectedFiles || selectedFiles.length == 0) {
      this.fileUploaderValueDescription = '';
    } else {
      if (selectedFiles.length == 1) {
        this.fileUploaderValueDescription = selectedFiles[0].name;
        this.fileUploaderValueTooltip = '';
      } else {
        this.fileUploaderValueDescription = `${selectedFiles.length} files selected`;
        this.fileUploaderValueTooltip = '';

        const selectedFilesNames: string[] = [];
        for (let i = 0; i < selectedFiles.length; i++) {
          selectedFilesNames.push(selectedFiles[i].name);
        }
        this.fileUploaderValueTooltip = selectedFilesNames.join('\n');
      }
    }
  }
}
